import { jCommentListSchema } from './comment';
import { jFileLittleSchema } from './file';
import { articleCreationSchema, jArticleListSchema, jArticleLittleSchema } from './article';
import { jObjectListSchema } from './object';
import { jGroupLittleSchema } from './group';
import { z, ZodType } from 'zod';

export const tvChannelTypeSchema = z.union([z.literal('DYNAMIC'), z.literal('SLIDESHOW')]);
export type TvChannelType = z.infer<typeof tvChannelTypeSchema>;

export const inferTVChannelCustomSchema = <T extends ZodType<TvChannelType>>(type: T) =>
    jObjectListSchema.extend({
        tvChannelType: type,
        tvChannelBackgroundColor: z.string().nullable(),
        tvChannelNumImages: z.number(),
        tvChannelGroupTarget: jGroupLittleSchema.optional(),
        tvChannelConfig: z.object({
            backgroundType: z.union([z.literal('color'), z.literal('image')]).optional(),
            globalShowComment: z.boolean().optional(),
            scaleImg: z.boolean().optional(),
            key: z.string(),
            contents: z
                .array(
                    z.object({
                        uri: z.string(),
                        dateEnd: z.string().optional(),
                        showComments: z.boolean().optional(),
                        order: z.number().optional(),
                    }),
                )
                .optional(),
        }),
    });
export type TVChannelCustom<T extends TvChannelType> = z.infer<
    ReturnType<typeof inferTVChannelCustomSchema<ZodType<T>>>
>;

export const tvChannelCustomListDynamicSchema = inferTVChannelCustomSchema(z.literal('DYNAMIC')).extend({
    tvChannelNumImages: z.literal(0),
});
export type TVChannelCustomListDynamic = z.infer<typeof tvChannelCustomListDynamicSchema>;

export const tvChannelCustomListSlideshowSchema = inferTVChannelCustomSchema(z.literal('SLIDESHOW'));
export type TVChannelCustomListSlideshow = z.infer<typeof tvChannelCustomListSlideshowSchema>;

export const tvChannelLittleSchema = jArticleLittleSchema.readonly();
export type TVChannelLittle = z.infer<typeof tvChannelLittleSchema>;

export const tvChannelDynamicListSchema = jArticleListSchema.and(tvChannelCustomListDynamicSchema).readonly();
export type TVChannelDynamicList = z.infer<typeof tvChannelDynamicListSchema>;

export const tvChannelSlideshowListSchema = jArticleListSchema.and(tvChannelCustomListSlideshowSchema).readonly();
export type TVChannelSlideshowList = z.infer<typeof tvChannelSlideshowListSchema>;

export const tvChannelCustomListSchema = jArticleListSchema
    .and(tvChannelCustomListDynamicSchema)
    .readonly()
    .or(tvChannelCustomListSlideshowSchema);
export type TVChannelCustomList = z.infer<typeof tvChannelCustomListSchema>;

export const tvChannelListSchema = tvChannelDynamicListSchema.or(tvChannelSlideshowListSchema);
export type TVChannelList = z.infer<typeof tvChannelListSchema>;

export const tvChannelCreationSchema = articleCreationSchema
    .and(tvChannelCustomListDynamicSchema.partial().or(tvChannelCustomListSlideshowSchema.partial()))
    .and(
        z.object({
            tvChannelType: tvChannelTypeSchema,
        }),
    )
    .readonly();
export type TVChannelCreation = z.infer<typeof tvChannelCreationSchema>;

export const tvChannelCreatePayloadSchema = z.object({
    tvChannelGroupTarget: z.array(z.string()).optional(),
    tvChannelNumImages: z.never().optional(),
});
export type TVChannelCreatePayload = z.infer<typeof tvChannelCreatePayloadSchema>;

export const tvChannelDynamicCommentSchema = jCommentListSchema
    .extend({
        _extend: z.object({
            userImageSecureUrl: z.string(),
            descriptionSecure: z.string(),
        }),
    })
    .readonly();
export type TVChannelDynamicComment = z.infer<typeof tvChannelDynamicCommentSchema>;

export const tvChannelDynamicArticleSchema = jArticleListSchema
    .extend({
        _extend: z
            .object({
                imageSecureUrl: z.string().optional(),
                descriptionSecure: z.string(),
                comments: z.array(tvChannelDynamicCommentSchema),
            })
            .optional(),
    })
    .readonly();
export type TVChannelDynamicArticle = z.infer<typeof tvChannelDynamicArticleSchema>;

export const tvChannelSlideshowFileSchema = jFileLittleSchema.extend({
    _extend: z.object({
        imageSecureUrl: z.string().optional(),
    }),
});
export type TVGetChannelSlideshowFile = z.infer<typeof tvChannelSlideshowFileSchema>;

export const tvGetConfigureDynamicChannelResponse = tvChannelDynamicListSchema.and(
    z.object({
        _extend: z.object({
            imageSecureUrl: z.string().optional(),
            articles: z.array(tvChannelDynamicArticleSchema).optional(),
        }),
    }),
);
export type TVGetConfigureDynamicChannelResponse = z.infer<typeof tvGetConfigureDynamicChannelResponse>;
export const ensureTVGetConfigureDynamicChannelResponse = (o: unknown): o is TVGetConfigureDynamicChannelResponse =>
    tvGetConfigureDynamicChannelResponse.safeParse(o).success;

export const tvGetConfigureSlideshowChannelResponse = tvChannelSlideshowListSchema.and(
    z.object({
        _extend: z.object({
            attachments: z.array(tvChannelSlideshowFileSchema).optional(),
        }),
    }),
);
export type TVGetConfigureSlideshowChannelResponse = z.infer<typeof tvGetConfigureSlideshowChannelResponse>;
export const ensureTVGetConfigureSlideshowChannelResponse = (o: unknown): o is TVGetConfigureSlideshowChannelResponse =>
    tvGetConfigureSlideshowChannelResponse.safeParse(o).success;

export const tvGetConfigureChannelResponseSchema = tvGetConfigureDynamicChannelResponse.or(
    tvGetConfigureSlideshowChannelResponse,
);
export type TVGetConfigureChannelResponse = z.infer<typeof tvGetConfigureChannelResponseSchema>;

export const tvGetChannelResponseSchema = z.object({
    interval: z.number(),
    channel: tvGetConfigureChannelResponseSchema,
});
export type TVGetChannelResponse = z.infer<typeof tvGetChannelResponseSchema>;
